import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import PerformingAssetsCard from "../components/PerformingAssetsCard";
import { InsightSecondaryMetrics } from "../components/InsightMetricLines";
import {
  FEMALES,
  GENDERS_PLACEHOLDER,
  MALES,
} from "../../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../../lib/i18next";
import { META } from "../../../../../utils/constants";
import {
  buildInsightMetricCells,
  getBestPerformanceRow,
} from "./MetaDemographicInsightsTables";
import { PERFORMING_ASSETS_BLUE } from "../components/performingAssetsStyles";

const DONUT_COLORS = [PERFORMING_ASSETS_BLUE, "#93C5FD", "#64748B"];
const DONUT_SIZE = 132;
const DONUT_STROKE = 18;

const getGenderRowLabel = ({ gender, t }) => {
  if (gender === "female") return t(FEMALES);
  if (gender === "male") return t(MALES);
  return gender ?? "—";
};

// Best row always gets the highlight blue; the rest use the softer colors
const getGenderRowColors = ({ rows, bestRow }) => {
  const others = DONUT_COLORS.slice(1);
  let otherIndex = 0;
  return rows.map((row) => {
    if (row === bestRow) return DONUT_COLORS[0];
    const color = others[otherIndex % others.length];
    otherIndex += 1;
    return color;
  });
};

const GenderDonut = ({ rows, colors }) => {
  const total = rows.reduce((sum, row) => sum + (row?.impressions || 0), 0);
  if (total <= 0) return null;

  const radius = (DONUT_SIZE - DONUT_STROKE) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = DONUT_SIZE / 2;
  let accumulated = 0;

  return (
    <Box
      component="svg"
      viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}
      sx={{ width: DONUT_SIZE, height: DONUT_SIZE, flexShrink: 0 }}
      aria-hidden
    >
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="#EEF2F6"
        strokeWidth={DONUT_STROKE}
      />
      {rows.map((row, index) => {
        const share = (row?.impressions || 0) / total;
        const dash = share * circumference;
        const offset = -accumulated;
        accumulated += dash;
        return (
          <circle
            key={row.gender || index}
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={colors[index]}
            strokeWidth={DONUT_STROKE}
            strokeDasharray={`${dash} ${circumference - dash}`}
            strokeDashoffset={offset}
            strokeLinecap="butt"
            transform={`rotate(-90 ${center} ${center})`}
          />
        );
      })}
    </Box>
  );
};

const MetaGenderDemographicInsightsCard = ({
  genderRows = [],
  getCostByMainCurrency,
}) => {
  const { t } = useTranslationApp();

  if (!genderRows?.length) return null;

  const bestRow = getBestPerformanceRow(genderRows);
  const rowColors = getGenderRowColors({ rows: genderRows, bestRow });

  return (
    <PerformingAssetsCard
      platform={META}
      title={t(GENDERS_PLACEHOLDER)}
      nextContent={
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
          }}
        >
          <GenderDonut rows={genderRows} colors={rowColors} />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
              flex: "1 1 140px",
              minWidth: 0,
            }}
          >
            {genderRows.map((row, index) => {
              const cells = buildInsightMetricCells({
                row,
                t,
                getCostByMainCurrency,
              });
              const label = getGenderRowLabel({ gender: row?.gender, t });
              return (
                <Box key={label}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: rowColors[index],
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontWeight: 600,
                        color:
                          row === bestRow
                            ? PERFORMING_ASSETS_BLUE
                            : "text.primary",
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                  <Box sx={{ pl: 2 }}>
                    <InsightSecondaryMetrics items={cells} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      }
    />
  );
};

export default MetaGenderDemographicInsightsCard;
